import type { AuthorizationCase } from '@/services/authorization-case.service';
import {
  FINDING_SEVERITY_LABELS,
  type Finding,
  type InspectionFindingSeverity,
} from '@/services/inspection.service';
import { CaseContextField, CaseContextFieldGrid } from './CaseContextField';

const SEVERITY_BADGE: Record<InspectionFindingSeverity, string> = {
  CRITICAL: 'bg-red-500/10 text-red-700',
  HIGH: 'bg-orange-500/10 text-orange-700',
  MEDIUM: 'bg-amber-500/10 text-amber-700',
  LOW: 'bg-green-500/10 text-green-700',
};

interface Props {
  kase: AuthorizationCase;
  finding: Finding | null;
}

export function FindingContextPanel({ kase, finding }: Props) {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-5 py-3.5">
        <h3 className="text-sm font-semibold text-foreground">Inspection Findings</h3>
      </div>
      <div className="px-5 py-4">
        {!kase.findingId ? (
          <p className="text-sm text-muted-foreground">
            Not applicable — this request originated from a procedure step, not an inspection
            finding.
          </p>
        ) : !finding ? (
          <p className="text-sm text-muted-foreground">Loading finding details…</p>
        ) : (
          <CaseContextFieldGrid>
            <CaseContextField
              label="Finding"
              value={`#${finding.findingNumber} — ${finding.title}`}
            />
            <CaseContextField
              label="Severity"
              value={
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_BADGE[finding.severity]}`}
                >
                  {FINDING_SEVERITY_LABELS[finding.severity]}
                </span>
              }
            />
            <CaseContextField
              label="Affected Component"
              value={finding.componentArea ?? 'Not recorded'}
            />
            <CaseContextField
              label="Authorization Status"
              value={finding.authorizationStatus ?? 'No prior authorization status'}
            />
            <div className="sm:col-span-2">
              <CaseContextField
                label="Recommendation"
                value={finding.recommendedAction ?? 'No recommendation recorded'}
              />
            </div>
          </CaseContextFieldGrid>
        )}
      </div>
    </div>
  );
}
